/**
 * Per-drawing Fibonacci level visibility editor.
 *
 * Opened from a single fib's right-click menu ("Levels…"). Lists the
 * globally-enabled fib levels with a checkbox each; unchecking one
 * hides that level for THIS fib only (writes the fib's `hiddenLevels`
 * override), leaving the global config and every other fib untouched.
 *
 * Presentational: the parent owns the fib + dispatches `updateFib`.
 * Same chrome as LineStyleDialog / FibLevelsDialog so the family of
 * popups feels consistent.
 */
import { useEffect } from "react";
import type { FibLevel } from "@/api/types";
import styles from "./FibDrawingLevelsDialog.module.css";

interface Props {
  open: boolean;
  /** All configured fib levels (global). Only the globally-enabled
   *  ones are listed — disabled levels are off everywhere, so there's
   *  nothing per-fib to toggle for them. */
  levels: FibLevel[];
  /** Ratios hidden for this fib. */
  hidden: number[];
  /** Fires with the next hidden-ratios array on every toggle. */
  onChange: (hidden: number[]) => void;
  /** Whether the connecting diagonal/trend line is hidden for this fib. */
  hideDiagonal: boolean;
  /** Fires with the next hide-diagonal value when toggled. */
  onToggleDiagonal: (hide: boolean) => void;
  /** Which side the level labels sit on for this fib. */
  labelSide: "left" | "right";
  /** Fires with the chosen side. */
  onLabelSide: (side: "left" | "right") => void;
  onClose: () => void;
}

export function FibDrawingLevelsDialog({
  open,
  levels,
  hidden,
  onChange,
  hideDiagonal,
  onToggleDiagonal,
  labelSide,
  onLabelSide,
  onClose,
}: Props) {
  // Close on Escape. Capture phase + stopPropagation so it dismisses
  // just this popup, not an underlying drawing/selection handler.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  const enabledLevels = levels.filter((l) => l.enabled);
  const hiddenSet = new Set(hidden);

  const toggle = (ratio: number, visible: boolean) => {
    const next = new Set(hidden);
    if (visible) next.delete(ratio);
    else next.add(ratio);
    onChange([...next]);
  };

  const showAll = () => onChange([]);
  const hideAll = () => onChange(enabledLevels.map((l) => l.ratio));

  return (
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <div
        className={styles.dialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fib-levels-title"
      >
        <header className={styles.header}>
          <h2 id="fib-levels-title" className={styles.title}>
            Levels for this fib
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          {enabledLevels.length === 0 ? (
            <div className={styles.empty}>
              No levels are enabled globally. Enable some via the
              Fibonacci tool's “Edit settings” first.
            </div>
          ) : (
            enabledLevels.map((l) => {
              const visible = !hiddenSet.has(l.ratio);
              return (
                <label key={l.ratio} className={styles.row}>
                  <input
                    type="checkbox"
                    className={styles.check}
                    checked={visible}
                    onChange={(e) => toggle(l.ratio, e.target.checked)}
                  />
                  <span
                    className={styles.swatch}
                    style={{ background: l.color }}
                    aria-hidden
                  />
                  <span className={styles.ratioLabel}>
                    {(l.ratio * 100).toFixed(1)}%
                  </span>
                </label>
              );
            })
          )}

          {/* Diagonal/trend line toggle — separate from the levels. */}
          <label className={`${styles.row} ${styles.diagonalRow}`}>
            <input
              type="checkbox"
              className={styles.check}
              checked={!hideDiagonal}
              onChange={(e) => onToggleDiagonal(!e.target.checked)}
            />
            <span className={styles.ratioLabel}>Diagonal (trend) line</span>
          </label>

          {/* Label side — handy for fibs nested inside another. */}
          <div className={styles.sideRow}>
            <span className={styles.ratioLabel}>Labels</span>
            <div className={styles.segmented} role="radiogroup">
              {(["left", "right"] as const).map((side) => (
                <button
                  key={side}
                  type="button"
                  role="radio"
                  aria-checked={labelSide === side}
                  className={`${styles.segment} ${labelSide === side ? styles.segmentActive : ""}`}
                  onClick={() => onLabelSide(side)}
                >
                  {side === "left" ? "Left" : "Right"}
                </button>
              ))}
            </div>
          </div>
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={hideAll}
            disabled={enabledLevels.length === 0}
          >
            Hide all
          </button>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={showAll}
            disabled={hidden.length === 0}
          >
            Show all
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onClose}
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}
